import { useState, useEffect } from 'react';
import SVG from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';

import { DealStatus } from 'components/common/types/Deal.types';
import { InputValue } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import { formatValue, FormatTypeEnum } from 'modules/I18N';
import { MAX_SAFE_INTEGER_ALLOWED_IN_BACKEND } from 'modules/api/constants';
import warningSvg from 'assets/icons/warning.svg';

import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { addToastNotification } from 'store/notification/reducer';
import { getCurrentLineAvailability } from 'store/dealManagement/selectors/availabilityAssets';

import InputNumber from 'lib/InputNumber';
import InfoMessage from 'components/common/Deal/InfoMessage';
import { INFO_TYPE } from 'components/common/Deal/InfoMessage/InfoMessage.types';

type FramesProps = {
  isDisabled: boolean;
};

const Frames = ({ isDisabled }: FramesProps): React.ReactElement => {
  const dispatch = useDispatch();
  const backupFormData = useSelector((state: Store) => state.dealManagement.backupFormData);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const id = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.id);
  const frames = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frames) ?? 0;
  const availability = useSelector(getCurrentLineAvailability);
  const { currencyCode, localeCode } = useSelector((state: Store) => state.publisher.configuration);
  const { allocatedFrames } = availability;
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const backupLine = backupFormData.lines.find((line) => line.id === id);

  const [showFramesMessage, setShowFramesMessage] = useState(false);

  useEffect(() => {
    setShowFramesMessage(!!(allocatedFrames && frames && allocatedFrames !== frames));
  }, [frames, allocatedFrames]);

  const changeFrames = (value: InputValue): void => {
    if (Number.isInteger(value) && Number(value) <= MAX_SAFE_INTEGER_ALLOWED_IN_BACKEND) {
      dispatch(changeDealCurrentLineData({ frames: value as number }));
    }
  };

  const onBlur = (): void => {
    if (Number(frames) && isPendingReservation && Number(frames) > (backupLine?.frames ?? 0)) {
      dispatch(changeDealCurrentLineData({ frames: backupLine?.frames }));
      dispatch(addToastNotification({ description: 'Error - Cannot Increase Frames Of DV360 Deal', type: 'Failure' }));
    }
  };

  const getFieldInfoMessage = (): string | null =>
    showFramesMessage
      ? `Your frame target of ${formatValue(frames, FormatTypeEnum.NUMBER, currencyCode, localeCode)} is ${
          frames < allocatedFrames ? 'less' : 'more'
        } than the allocated number of frames ${formatValue(
          allocatedFrames,
          FormatTypeEnum.NUMBER,
          currencyCode,
          localeCode,
        )}. Input your delivery targets for this deal line`
      : null;

  const fieldInfoMessage = getFieldInfoMessage();

  return (
    <div>
      <label
        htmlFor="frames"
        className="sub-header-base text-neutral-950-opacity-60 mb-2 inline-block"
        data-test-id="frames-header"
      >
        Frames
      </label>
      <div className="relative">
        <InputNumber
          name="frames"
          placeholder="Add number of frames…"
          value={frames}
          onBlur={onBlur}
          onChange={changeFrames}
          maximumFractionDigits={0}
          isDisabled={isDisabled}
          dataTestId="frames-input"
        />
        {fieldInfoMessage ? (
          <div className="absolute top-2 -right-8">
            <InfoMessage type={INFO_TYPE.TOOLTIP} icon={<SVG src={warningSvg} className="fill-current text-yellow" />}>
              <div className="w-48">{fieldInfoMessage}</div>
            </InfoMessage>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Frames;
